/**
 * Transcript Panel Component
 * 
 * Displays conversation transcript in a chat interface format
 * with slide-in/out animation and auto-scroll.
 */

import React, { useEffect, useRef } from 'react';
import { useTranscript } from '../../contexts/TranscriptContext';
import './transcript-panel.scss';

const AGENT_NAMES: Record<string, string> = {
  'greeting_agent':      'Dubai Police 999',
  'intent_agent':        'Dubai Police 999',
  'followup_agent':      'Dubai Police 999',
  'inquiry_agent':       'Dubai Police 999',
  'image_upload_agent':  'Dubai Police 999',
  'agency_agent':        'Dubai Police 999',
  'incident_card_agent': 'Dubai Police 999',
  'closing_agent':       'Dubai Police 999',
  'human_handoff_agent': 'Dubai Police 999',
  // Legacy
  'welcome_agent':       'Dubai Police 999',
};

export const TranscriptPanel: React.FC = () => {
  const { messages, isPanelOpen, clearTranscript, markAsRead, togglePanel } = useTranscript();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isPanelOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isPanelOpen]);

  // Mark as read when panel is opened
  useEffect(() => {
    if (isPanelOpen) {
      markAsRead();
    }
  }, [isPanelOpen, markAsRead]);

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getAgentDisplayName = (agentId?: string): string => {
    if (!agentId) return 'Dubai Police Assistant';
    return AGENT_NAMES[agentId] || agentId;
  };

  return (
    <div className={`transcript-panel ${isPanelOpen ? 'open' : 'closed'}`} ref={containerRef}>
      <div className="transcript-header">
        <div className="header-title">
          <span className="material-symbols-outlined">chat</span>
          <h3>Conversation</h3>
        </div>
        <div className="header-actions">
          <button
            className="icon-button icon-delete"
            onClick={clearTranscript}
            title="Clear transcript"
            disabled={messages.length === 0}
          >
            <span className="material-symbols-outlined">delete</span>
          </button>
          <button
            className="icon-button icon-close-panel"
            onClick={togglePanel}
            title="Close"
            aria-label="Close transcript"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
      </div>

      <div className="transcript-messages">
        {messages.length === 0 ? (
          <div className="empty-state">
            <span className="material-symbols-outlined">chat_bubble_outline</span>
            <p>No conversation yet</p>
            <span className="empty-hint">Start talking to begin</span>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message-wrapper ${message.role}`}
              >
                <div className="message-bubble">
                  <div className="message-icon">
                    {message.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="message-content">
                    <div className="message-text">{message.content}</div>
                    <div className="message-meta">
                      {message.role === 'assistant' && message.agentId && (
                        <span className="agent-name">
                          {getAgentDisplayName(message.agentId)}
                        </span>
                      )}
                      <span className="message-time">
                        {formatTime(message.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </div>
  );
};
